<template>
  <div class="flex items-start px-4 pt-2 pb-4 sm:px-6 sm:pb-4">
    <div class="flex flex-col w-full">
      <p v-if="successMessage" class="alert alert--good mt-0">
        {{ successMessage }}
      </p>
      <p v-if="errorMessage" class="alert alert--bad mt-0">
        {{ errorMessage }}
      </p>

      <transition name="fade">
        <div
          v-if="!this.connectedIntegration"
        >
          <form @submit.prevent="connectToJira">
            <input
              v-model="jiraSiteURL"
              name="integrations_jira_integration[site_url]"
              type="text"
              class="input"
              :placeholder="$t('Jira project URL (e.g project.atlassian.net)')"
            />

            <input
              v-model="jiraUsername"
              name="integrations_jira_integration[username]"
              type="email"
              class="input mt-4"
              :placeholder="$t('Jira user email')"
            />

            <p class="text-neutral-700 text-sm mt-1">
              {{ $t('The user must have admin permissions to the project') }}
            </p>

            <input
              v-model="jiraAPIToken"
              name="integrations_jira_integration[api_token]"
              type="text"
              class="input mt-4"
              :placeholder="$t('Jira API Token')"
            />

            <p class="text-neutral-700 text-sm mt-1">
              <a
                class="underline"
                href="https://id.atlassian.com/manage-profile/security/api-tokens"
                target="_blank"
                >{{ $t('Click here') }}</a
              >
              {{ $t('to generate your Jira API Token') }}
            </p>

            <div class="mt-4 h-10">
              <button
                class="btn-main rounded"
                type="submit"
                :disabled="areFieldsEmpty"
              >
                <span :class="loaderState.connectingToJira && 'mr-3'"
                  >{{ $t('Connect to Jira') }}</span
                >
                <loader
                  v-if="loaderState.connectingToJira"
                  border-color="#fff"
                />
              </button>
            </div>
          </form>
        </div>

        <div v-else>
          <form @submit.prevent="saveConfiguration">
            <p class="text-sm leading-5 text-neutral-600 mb-2">
              {{ $t('Project') }}
            </p>
            <div class="flex">
              <div class="relative lg:w-1/2 xl:w-1/2">
                <select
                  name="integrations_jira_integration[default_project]"
                  class="input select"
                  v-model="selectedProject"
                  @change="getIssueTypesofSelectedProject()"
                >
                  <option disabled value="" selected>{{ $t('Select project') }}</option>
                  <option
                    v-for="option in connectedIntegration.projects_with_ids"
                    :key="option[0]"
                    :value="option[1]"
                    :selected="
                      option[1] == connectedIntegration.default_project
                    "
                  >
                    {{ option[0] }}
                  </option>
                </select>
              </div>
              <loader
                class="ml-3"
                v-if="loaderState.loadingIssueTypes"
                border-color="#6b7280"
              />
            </div>

            <p class="text-sm leading-5 text-neutral-600 mt-3 mb-2">
              {{ $t('Issue type') }}
            </p>

            <div class="flex">
              <div class="relative lg:w-1/2 xl:w-1/2">
                <select
                  name="integrations_jira_integration[default_issue_type]"
                  class="input select"
                >
                  <option disabled value="" :selected="isDefault"
                    >{{ $t('Select issue type') }}</option
                  >
                  <option
                    v-for="option in projectIssueTypes"
                    :key="option[0]"
                    :value="option[1]"
                    :selected="
                      option[1] === connectedIntegration.default_issue_type
                    "
                  >
                    {{ option[0] }}
                  </option>
                </select>
              </div>
              <loader
                class="ml-3"
                v-if="loaderState.issuesLoader"
                border-color="#6b7280"
              />
            </div>

            <div class="flex mt-4 h-10">
              <button
                class="btn-secondary h-10"
                @click.prevent="disconnect()"
              >
                <span>{{ $t('Disconnect') }}</span>
              </button>

              <button
                class="btn-main ml-2 text-sm w-1/6 flex justify-center rounded"
                type="submit"
              >
                <span :class="loaderState.savingConfig && 'mr-3'">{{ $t('Save') }}</span>
                <loader v-if="loaderState.savingConfig" border-color="#fff" />
              </button>
            </div>
          </form>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import loader from "../spinner";
import axios from "axios";

export default {
  name: "jira-integration",
  props: ["resource-url", "project-id", "integration"],
  data() {
    return {
      connectedIntegration: this.integration,
      jiraSiteURL: "",
      jiraUsername: "",
      jiraAPIToken: "",
      selectedProject: "",
      selectedIssueType: "",
      successMessage: "",
      errorMessage: "",
      projectIssueTypes: [],
      loaderState: {
        connectingToJira: false,
        savingConfig: false,
        loadingIssueTypes: false,
        issuesLoader: false,
      },
    };
  },
  components: { loader },
  created() {
    this.initializeState();
  },
  mounted() {
    this.initializeState();
  },
  computed: {
    areFieldsEmpty() {
      return !this.jiraSiteURL || !this.jiraUsername || !this.jiraAPIToken;
    },
    isDefault() {
      return !this.projectIssueTypes
        ?.flat()
        .includes(this.connectedIntegration.default_issue_type);
    },
  },
  methods: {
    connectToJira(event) {
      this.resetMessages();
      this.loaderState.connectingToJira = true;
      let formData = new FormData(event.target);

      let projectUrl = formData.get("integrations_jira_integration[site_url]");
      if (this.urlDoesNotContainProtocol(projectUrl)) {
        formData.set(
          "integrations_jira_integration[site_url]",
          "https://" + projectUrl
        );
      }

      formData.append(
        "integrations_jira_integration[project_id]",
        this.projectId
      );

      Rails.ajax({
        url: this.resourceUrl,
        type: "POST",
        dataType: "json",
        data: formData,
        success: (data) => {
          this.loaderState.connectingToJira = false;
          this.successMessage = this.$t("Account connected successfully");
          this.connectedIntegration = data;
          this.setDefaultState();
          this.$emit("updateIntegration", this.connectedIntegration);
        },
        error: (response, status) => {
          console.log(response);
          this.loaderState.connectingToJira = false;
          this.errorMessage = response.error || status;
        },
      });
    },
    async getIssueTypesofSelectedProject() {
      this.loaderState.loadingIssueTypes = true;
      this.resetMessages();
      this.fetchIssueTypes()
        .then((data) => {
          this.projectIssueTypes = data.data.issue_types;
          this.loaderState.loadingIssueTypes = false;
        })
        .catch((error) => {
          console.log(error);
          this.loaderState.loadingIssueTypes = false;
          this.errorMessage = error.data.error || error;
          this.loaderState.loadingIssueTypes = false;
        });
    },
    saveConfiguration(event) {
      this.resetMessages();
      this.loaderState.savingConfig = true;
      let formData = new FormData(event.target);

      Rails.ajax({
        url: this.resourceUrl + `/${this.connectedIntegration.id}`,
        type: "PUT",
        dataType: "json",
        data: formData,
        success: (data) => {
          this.loaderState.savingConfig = false;
          this.successMessage = this.$t("Configuration saved successfully");
        },
        error: (response, status) => {
          console.log(response);
          this.loaderState.savingConfig = false;
          this.errorMessage = response.error || status;
        },
      });
    },
    disconnect() {
      this.resetMessages();
      Rails.ajax({
        url: this.resourceUrl + `/${this.connectedIntegration.id}`,
        type: "DELETE",
        success: (data) => {
          this.connectedIntegration = null;
          this.resetFields();
          this.$emit("updateIntegration", null);
        },
        error: (response, status) => {
          console.log(response);
          this.errorMessage = response.error || status;
        },
      });
    },
    fetchIssueTypes() {
      return axios({
        method: "GET",
        url:
          this.resourceUrl +
          `/${this.connectedIntegration.id}/issue_types?project=${this.selectedProject}`,
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
            .content,
        },
      });
    },
    resetMessages() {
      this.successMessage = "";
      this.errorMessage = "";
    },
    resetFields() {
      this.jiraSiteURL = "";
      this.jiraUsername = "";
      this.jiraAPIToken = "";
      this.selectedProject = "";
      this.selectedIssueType = "";
    },
    initializeState() {
      if (this.connectedIntegration?.default_project) {
        this.selectedProject = this.connectedIntegration.default_project;
        this.loaderState.issuesLoader = true;
        this.fetchIssueTypes()
          .then((data) => {
            this.projectIssueTypes = data.data.issue_types;
            this.loaderState.issuesLoader = false;
          })
          .catch((error) => {
            console.log(error);
            this.loaderState.issuesLoader = false;
          });
      }
    },
    urlDoesNotContainProtocol(url) {
      return !url.match(
        new RegExp(
          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/
        )
      );
    },
    setDefaultState() {
      this.selectedProject = this.connectedIntegration.default_project;
      this.getIssueTypesofSelectedProject();
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
